import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Icon } from './Brand'
import { tintMap } from '../data'
import AddAgentWizard from './AddAgentWizard'
import {
  agentActivityPresentation,
  agentTelemetryLimitations,
  buildAgentRoster,
  canManageAgent,
  titleizeAgent,
} from '../collections/agents'

const TINTS = ['lav', 'teal', 'coral', 'peach']
const statusPresentation = {
  enabled: { label: 'Enabled', className: 'live' },
  degraded: { label: 'Degraded', className: 'running' },
  disabled: { label: 'Disabled', className: 'idle' },
}

const iconFor = (agent) => ({
  payment_matching: 'payments',
  mail_management: 'mark_email_read',
  reply_drafting: 'edit_note',
  collect_it: 'auto_awesome',
}[agent.key] || 'smart_toy')

function Status({ agent }) {
  const presentation = statusPresentation[agent.health?.status] || statusPresentation.degraded
  return <span className={`status ${presentation.className}`}><span className="dot" />{presentation.label}</span>
}

function timestamp(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('en-IL', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

function metricValue(key, value) {
  if (typeof value !== 'number') return String(value ?? '—')
  if (/(precision|coverage|rate)/i.test(key) && value >= 0 && value <= 1) return `${(value * 100).toFixed(1)}%`
  return new Intl.NumberFormat('en-IL', { maximumFractionDigits: 1 }).format(value)
}

function RosterCard({ agent, tint, selected, onClick, index }) {
  const t = tintMap[tint] || tintMap.lav
  const trigger = agent.trigger?.type ? titleizeAgent(agent.trigger.type) : agent.tool_name
  return (
    <motion.div
      className="agent clickable"
      onClick={onClick}
      style={selected ? { borderColor: t.solid, boxShadow: 'var(--sh-md)' } : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.06 + index * 0.06, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="agent-top">
        <div className="agent-ic" style={{ background: t.bg }}>
          <Icon name={iconFor(agent)} style={{ color: t.fg }} />
        </div>
        <div className="agent-title-block">
          <div className="agent-name">{agent.display_name}</div>
          <div className="agent-role mono">{trigger}</div>
        </div>
        <Status agent={agent} />
      </div>
      <div className="agent-source-row">
        <span className={`agent-source ${agent.system_owned ? 'system' : ''}`}>
          {agent.system_owned ? 'System agent' : agent.source_label}
        </span>
        {agent.activity?.contains_fixture_data && <span className="agent-source fixture">Demo telemetry</span>}
        {agent.model?.model && <span className="mono">{agent.model.model}</span>}
      </div>
      <div className="agent-job">
        <div className="jl">{agent.trigger ? 'Trigger' : 'Capabilities granted'}</div>
        <div className="jt">{agent.trigger?.description || agent.description || trigger || 'No trigger details'}</div>
      </div>
      {agent.children.length > 0 && (
        <div className="agent-worker-summary">
          <Icon name="account_tree" />
          {agent.children.map((child) => child.display_name).join(', ')}
        </div>
      )}
      {agent.system_owned && (
        <div className="agent-card-stats">
          <span><b>{agent.queue?.supported ? agent.queue.pending ?? '—' : '—'}</b> queued</span>
          <span><b>{agent.activity?.processed_count ?? '—'}</b> processed</span>
          <span><b>{agent.activity?.last_run_at ? timestamp(agent.activity.last_run_at) : 'Never'}</b> last run</span>
        </div>
      )}
    </motion.div>
  )
}

function CapabilityList({ values, empty = 'no access' }) {
  return <div className="ctl-caps agent-detail-caps">
    {values.length === 0
      ? <span className="ctl-cap muted">{empty}</span>
      : values.map((value, index) => <span className="ctl-cap" key={`${value}-${index}`}>{value}</span>)}
  </div>
}

function SystemAgentDetail({ agent, onNavigate }) {
  const model = agent.model || {}
  const activity = agent.activity || {}
  const queue = agent.queue || {}
  const metrics = agent.kpis?.metrics && typeof agent.kpis.metrics === 'object'
    ? Object.entries(agent.kpis.metrics)
    : []
  const latestActivity = agentActivityPresentation(activity.latest)
  const telemetryLimitations = agentTelemetryLimitations(activity, queue)

  return <>
    <div className="agent-runtime-grid">
      <div><span>Trigger</span><b>{titleizeAgent(agent.trigger?.type || 'Unknown')}</b><small>{agent.trigger?.description || 'No trigger description reported'}</small></div>
      <div><span>Model</span><b>{model.model || 'Not configured'}</b><small>{model.provider || model.model_configuration?.provider || 'Provider unavailable'}</small></div>
      <div><span>Queue</span><b>{queue.supported ? `${queue.pending ?? 0} pending` : 'Not reported'}</b><small>{queue.supported ? `${queue.total ?? queue.pending ?? 0} total tracked` : 'Queue telemetry unavailable'}</small></div>
      <div><span>Last run</span><b>{timestamp(activity.last_run_at)}</b><small>{activity.latency_ms == null ? 'Latency unavailable' : `${activity.latency_ms} ms latency`}</small></div>
    </div>
    {(activity.processed_count != null || activity.last_success_at || activity.last_failure_at) && <div className="agent-activity-facts">
      <span><b>{activity.processed_count ?? '—'}</b> processed</span>
      <span><b>{timestamp(activity.last_success_at)}</b> last success</span>
      <span><b>{timestamp(activity.last_failure_at)}</b> last failure</span>
      {activity.source && <span><b>{titleizeAgent(activity.source)}</b> telemetry source</span>}
    </div>}

    {(agent.health.status_reasons || []).length > 0 && <div className="agent-health-note">
      <Icon name="info" />
      <span>{agent.health.status_reasons.join(' · ')}</span>
    </div>}

    <div className="ad-sub">Declared capabilities</div>
    <CapabilityList values={agent.capabilities} />

    <div className="ad-sub">LLM-callable tools</div>
    <div className="agent-tool-list">
      {agent.tools.length === 0 && <div className="ph-sub">No LLM-callable tools reported.</div>}
      {agent.tools.map((tool) => <div key={tool.name}>
        <Icon name="construction" /><span><b>{titleizeAgent(tool.name)}</b><small>{titleizeAgent(tool.access || 'unknown access')} · {tool.external_effect ? 'External effect' : 'No external effect'}</small></span>
      </div>)}
    </div>

    {agent.deterministic_effects.length > 0 && <>
      <div className="ad-sub">Deterministic application effects</div>
      <div className="agent-effect-list">
        {agent.deterministic_effects.map((effect) => <div key={effect.name}>
          <span><b>{titleizeAgent(effect.name)}</b><small>{effect.reason || `Owned by ${effect.execution_owner || 'the application'}`}</small></span>
          <span className={`agent-effect-status ${effect.status}`}>{titleizeAgent(effect.status)}</span>
        </div>)}
      </div>
    </>}

    {agent.children.length > 0 && <>
      <div className="ad-sub">Internal workers</div>
      <div className="agent-worker-list">
        {agent.children.map((child) => <div key={child.key}>
          <div className="agent-ic"><Icon name={iconFor(child)} /></div>
          <span><b>{child.display_name}</b><small>{child.description || child.trigger?.description || 'Internal capability'}</small></span>
          <Status agent={child} />
        </div>)}
      </div>
    </>}

    {metrics.length > 0 && <>
      <div className="ad-sub">Latest KPI evaluation {agent.kpis.passed != null && <span className={agent.kpis.passed ? 'agent-kpi-pass' : 'agent-kpi-fail'}>{agent.kpis.passed ? 'Passed' : 'Failed'}</span>}</div>
      <div className="agent-kpi-grid">
        {metrics.map(([key, value]) => <div key={key}><b>{metricValue(key, value)}</b><span>{titleizeAgent(key)}</span></div>)}
      </div>
    </>}

    <div className="ad-sub">Latest Collections activity</div>
    {latestActivity ? <div className="agent-latest-activity">
      <Icon name="history" />
      <span><b>{latestActivity.label}</b><small>{latestActivity.fixture ? 'Demo fixture · ' : ''}{timestamp(latestActivity.timestamp)} · {titleizeAgent(latestActivity.eventType)}</small></span>
    </div> : <div className="ph-sub">No persisted Collections activity reported yet.</div>}
    {telemetryLimitations.length > 0 && <div className="agent-telemetry-note"><Icon name="privacy_tip" />{telemetryLimitations.join(' · ')}</div>}

    {agent.deep_links.length > 0 && <div className="agent-deep-links">
      {agent.deep_links.map((link) => <button key={`${link.label}-${link.view}`} className="chat-suggest" onClick={() => onNavigate(link.view, link.target || {})}>
        {link.label}<Icon name="arrow_forward" />
      </button>)}
    </div>}
  </>
}

function GenericActivity({ agent, live, tint }) {
  const recent = live.filter((event) => (event.agent || '').toLowerCase() === agent.name.toLowerCase())
  return <>
    <div className="ad-sub">Capabilities granted</div>
    <CapabilityList values={agent.capabilities} empty={agent.custom ? 'prompt-only' : 'no access'} />
    {agent.mcp_endpoint && <div className="ph-sub mono agent-mcp">MCP: {agent.mcp_endpoint}</div>}
    <div className="ad-sub">Recent activity</div>
    <div className="feed">
      {recent.length === 0 && <div className="ph-sub">No requests routed to this agent yet — ask something in Chat.</div>}
      {recent.map((row) => <div className="feed-row" key={row.id}>
        <div className="feed-ic" style={{ background: tint.bg }}><Icon name={row.icon} style={{ color: tint.fg }} /></div>
        <div className="ft"><b>{agent.display_name}</b> <span>{row.text}</span></div>
        <div className="fm">{timestamp(row.when)}</div>
      </div>)}
    </div>
  </>
}

export default function AgentsPage({
  agents = [],
  collectionsRegistry = { data: null, availability: 'loading', reason: null },
  live = [],
  onChanged = () => {},
  onNavigate = () => {},
  onRefresh = () => {},
}) {
  const [selectedId, setSelectedId] = useState(null)
  const [wizardOpen, setWizardOpen] = useState(false)
  const roster = buildAgentRoster(agents, collectionsRegistry.data)
  const selected = roster.agents.find((agent) => agent.roster_id === selectedId) || roster.agents[0]
  const tintFor = (agent) => TINTS[Math.max(0, roster.agents.findIndex((item) => item.roster_id === agent.roster_id)) % TINTS.length]

  async function mutate(path) {
    try { await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }) }
    finally { onChanged() }
  }

  const refreshing = ['loading', 'refreshing'].includes(collectionsRegistry.availability)
  const availabilityCopy = collectionsRegistry.availability === 'loading'
    ? 'Loading Collections system agents…'
    : collectionsRegistry.reason

  return <>
    <motion.div className="page-head" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
      <div>
        <div className="eyebrow">Your workforce</div>
        <h1>Agents</h1>
        <p className="sub">{roster.visibleCount} agents · grouped by registry and workspace</p>
      </div>
      <div className="agent-head-actions">
        <button className="chat-suggest agent-refresh" disabled={refreshing} onClick={onRefresh}><Icon name="refresh" />{refreshing ? 'Refreshing…' : 'Refresh'}</button>
        <button className="chat-send add-agent-open" onClick={() => setWizardOpen(true)}><Icon name="add" /> Add agent</button>
      </div>
    </motion.div>

    {['loading', 'unavailable'].includes(collectionsRegistry.availability) && <div className={`agent-registry-notice ${collectionsRegistry.availability}`}>
      <Icon name={collectionsRegistry.availability === 'loading' ? 'progress_activity' : 'cloud_off'} />
      <span><b>Collections roster {collectionsRegistry.availability === 'loading' ? 'loading' : 'unavailable'}</b>{availabilityCopy} Generic agents remain available.</span>
    </div>}

    {roster.groups.map((group) => <section className="agent-roster-group" key={group.key}>
      <div className="section-label"><span>{group.label}</span><small>{group.description} · {group.agents.length}</small></div>
      <div className="agent-grid">
        {group.agents.map((agent, index) => <RosterCard key={agent.roster_id} agent={agent} tint={tintFor(agent)} selected={agent.roster_id === selected?.roster_id} onClick={() => setSelectedId(agent.roster_id)} index={index} />)}
      </div>
    </section>)}

    {roster.agents.length === 0 && <div className="panel agent-empty"><Icon name="smart_toy" /><b>No agents reported yet</b><span>The generic registry is still loading or empty.</span></div>}

    {selected && <>
      <div className="section-label">Detail</div>
      <AnimatePresence mode="wait">
        <motion.div key={selected.roster_id} className="panel agent-detail" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
          <div className="ad-head">
            <div className="agent-ic" style={{ background: (tintMap[tintFor(selected)] || tintMap.lav).bg }}><Icon name={iconFor(selected)} style={{ color: (tintMap[tintFor(selected)] || tintMap.lav).fg }} /></div>
            <div><h3>{selected.display_name}</h3><div className="ph-sub mono">{selected.source_label} · {selected.tool_name}</div></div>
            <Status agent={selected} />
            {canManageAgent(selected) && <div className="ad-actions">
              {selected.enabled && <button className="chat-suggest" onClick={() => mutate(`/api/agents/${selected.name}/disable`)}>Disable</button>}
              <button className="chat-suggest ad-delete" onClick={() => mutate(`/api/agents/${selected.name}/delete`)}>Delete</button>
            </div>}
          </div>
          {selected.description && <p className="approval-detail">{selected.description}</p>}
          {selected.system_owned
            ? <SystemAgentDetail agent={selected} onNavigate={onNavigate} />
            : <GenericActivity agent={selected} live={live} tint={tintMap[tintFor(selected)] || tintMap.lav} />}
        </motion.div>
      </AnimatePresence>
    </>}

    {wizardOpen && <AddAgentWizard onClose={() => setWizardOpen(false)} onCreated={onChanged} />}
  </>
}
